/**
 * 全息故障效果 - Hologram Glitch Effect
 * 
 * 效果说明：
 * - 全息投影感：RGB 分离、扫描线、透明渐变
 * - 故障艺术：随机位移、色彩失真、闪烁
 * - 赛博朋克风格：霓虹色、数字感
 * 
 * 技术亮点：
 * - RGB 色彩分离（红/绿/蓝层偏移）
 * - 扫描线动画（从上到下）
 * - 随机位移贴图（故障感）
 * - 透明度渐变（全息感）
 * - Spring 动画驱动
 * 
 * 适用场景：
 * - 科技品牌宣传片
 * - 产品发布视频
 * - 转场效果
 * - 赛博朋克主题内容
 * 
 * @created 2026-03-10
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 配置参数
const CONFIG = {
  // 文本配置
  text: 'HOLOGRAM',
  fontSize: 120,
  fontFamily: 'Arial Black, sans-serif',
  
  // 颜色配置
  colors: {
    cyan: '#00FFFF',
    magenta: '#FF00FF',
    yellow: '#FFFF00',
    white: '#FFFFFF',
  },
  
  // 动画配置
  glitchIntensity: 10, // 故障强度（像素）
  scanlineSpeed: 0.5, // 扫描线速度
  flickerFrequency: 0.3, // 闪烁频率
  
  // Spring 配置
  springConfig: {
    damping: 12,
    stiffness: 200,
    mass: 0.5,
  },
};

/**
 * RGB 色彩分离组件
 * 创建红、绿、蓝三层文字，通过偏移产生全息感
 */
const RGBSplitText: React.FC<{
  text: string;
  frame: number;
  intensity: number;
}> = ({ text, frame, intensity }) => {
  // 使用固定种子避免随机抖动
  const seed = 12345;
  const glitchX = Math.sin(frame * 0.1 + seed) * intensity;
  const glitchY = Math.cos(frame * 0.1 + seed) * intensity * 0.5;
  
  const commonStyle: React.CSSProperties = {
    position: 'absolute',
    fontSize: CONFIG.fontSize,
    fontFamily: CONFIG.fontFamily,
    fontWeight: 'bold',
    mixBlendMode: 'screen',
  };
  
  return (
    <>
      {/* 红色层 */}
      <div
        style={{
          ...commonStyle,
          color: 'rgba(255, 0, 0, 0.7)',
          transform: `translate(${glitchX}px, ${glitchY}px)`,
        }}
      >
        {text}
      </div>
      
      {/* 绿色层 */}
      <div
        style={{
          ...commonStyle,
          color: 'rgba(0, 255, 0, 0.7)',
          transform: `translate(${-glitchX}px, ${glitchY}px)`,
        }}
      >
        {text}
      </div>
      
      {/* 蓝色层 */}
      <div
        style={{
          ...commonStyle,
          color: 'rgba(0, 0, 255, 0.7)',
          transform: `translate(${glitchX}px, ${-glitchY}px)`,
        }}
      >
        {text}
      </div>
    </>
  );
};

/**
 * 扫描线组件
 * 从上到下的扫描线动画
 */
const Scanlines: React.FC<{
  frame: number;
  height: number;
}> = ({ frame, height }) => {
  const scanlineY = interpolate(
    frame,
    [0, 60],
    [-height / 2, height * 1.5],
    { extrapolateRight: 'wrap' }
  );
  
  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '4px',
        background: `linear-gradient(
          to bottom,
          transparent,
          ${CONFIG.colors.cyan}40,
          ${CONFIG.colors.white}80,
          ${CONFIG.colors.cyan}40,
          transparent
        )`,
        transform: `translateY(${scanlineY}px)`,
        boxShadow: `0 0 20px ${CONFIG.colors.cyan}`,
      }}
    />
  );
};

/**
 * 故障条纹组件
 * 随机出现的水平条纹
 */
const GlitchBars: React.FC<{
  frame: number;
  width: number;
  height: number;
}> = ({ frame, width, height }) => {
  const bars = [];
  const numBars = 3;
  
  for (let i = 0; i < numBars; i++) {
    // 使用固定种子 + 索引避免抖动
    const seed = i * 1000 + Math.floor(frame / 5) * 100;
    const appear = Math.sin(seed) > 0.7;
    
    if (!appear) continue;
    
    const barY = Math.abs(Math.sin(seed * 1.5)) * height;
    const barHeight = 5 + Math.abs(Math.sin(seed * 2)) * 15;
    const barOpacity = 0.3 + Math.abs(Math.sin(seed * 3)) * 0.4;
    
    bars.push(
      <div
        key={i}
        style={{
          position: 'absolute',
          width: '100%',
          height: barHeight,
          top: barY,
          background: `rgba(255, 255, 255, ${barOpacity})`,
          mixBlendMode: 'overlay',
        }}
      />
    );
  }
  
  return <>{bars}</>;
};

/**
 * 噪点效果组件
 * 全屏噪点覆盖
 */
const Noise: React.FC<{
  frame: number;
  opacity: number;
}> = ({ frame, opacity }) => {
  // 使用固定种子生成稳定的噪点
  const seed = Math.floor(frame / 2) * 1000;
  const noiseOpacity = (Math.sin(seed) * 0.5 + 0.5) * opacity;
  
  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: `radial-gradient(
          circle at center,
          transparent 0%,
          rgba(255, 255, 255, ${noiseOpacity * 0.1}) 50%,
          rgba(255, 255, 255, ${noiseOpacity * 0.3}) 100%
        )`,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
      }}
    />
  );
};

/**
 * 主组件：全息故障效果
 */
export const HologramGlitchEffect: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  
  // Spring 动画：文字入场
  const textProgress = spring({
    frame,
    fps: 30,
    config: CONFIG.springConfig,
  });
  
  // 文字缩放和透明度
  const textScale = interpolate(textProgress, [0, 1], [0.8, 1]);
  const textOpacity = interpolate(textProgress, [0, 1], [0, 1]);
  
  // 全息透明度渐变（从下到上）
  const hologramGradient = `linear-gradient(
    to top,
    ${CONFIG.colors.cyan}00,
    ${CONFIG.colors.cyan}40 20%,
    ${CONFIG.colors.cyan}80 50%,
    ${CONFIG.colors.cyan}40 80%,
    ${CONFIG.colors.cyan}00
  )`;
  
  // 闪烁效果
  const flickerSeed = Math.floor(frame * CONFIG.flickerFrequency);
  const flickerOpacity = Math.sin(flickerSeed) > 0.8 ? 0.7 : 1;
  
  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 背景全息渐变 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: hologramGradient,
          opacity: 0.3,
        }}
      />
      
      {/* 扫描线 */}
      <Scanlines frame={frame} height={height} />
      
      {/* 故障条纹 */}
      <GlitchBars frame={frame} width={width} height={height} />
      
      {/* RGB 分离文字 */}
      <div
        style={{
          position: 'relative',
          transform: `scale(${textScale})`,
          opacity: textOpacity * flickerOpacity,
        }}
      >
        {/* RGB 分离层 */}
        <RGBSplitText
          text={CONFIG.text}
          frame={frame}
          intensity={CONFIG.glitchIntensity}
        />
        
        {/* 主文字层 */}
        <div
          style={{
            position: 'relative',
            fontSize: CONFIG.fontSize,
            fontFamily: CONFIG.fontFamily,
            fontWeight: 'bold',
            color: CONFIG.colors.white,
            textShadow: `
              0 0 10px ${CONFIG.colors.cyan},
              0 0 20px ${CONFIG.colors.cyan},
              0 0 40px ${CONFIG.colors.cyan},
              0 0 80px ${CONFIG.colors.magenta}
            `,
            mixBlendMode: 'screen',
          }}
        >
          {CONFIG.text}
        </div>
      </div>
      
      {/* 噪点效果 */}
      <Noise frame={frame} opacity={0.1} />
      
      {/* 边框发光 */}
      <div
        style={{
          position: 'absolute',
          width: '90%',
          height: '80%',
          border: `2px solid ${CONFIG.colors.cyan}40`,
          boxShadow: `
            inset 0 0 50px ${CONFIG.colors.cyan}20,
            0 0 30px ${CONFIG.colors.cyan}40
          `,
          opacity: 0.6,
        }}
      />
    </AbsoluteFill>
  );
};

/**
 * Composition 配置
 */
export const HologramGlitchEffectComposition: React.FC = () => {
  return <HologramGlitchEffect />;
};
